import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, SlidersHorizontal, X, Lock, ArrowRight, Eye } from "lucide-react";
import { listCreators } from "../lib/api";
import CreatorCard from "../components/CreatorCard";
import { GAMES, QUALITIES, SERVICES, LANGUAGES } from "../lib/constants";
import { useUser } from "../context/UserContext";
import usePageMeta from "../lib/usePageMeta";

const SORTS = [
  { key: "rating", label: "Rating" },
  { key: "price_asc", label: "Price ↑" },
  { key: "price_desc", label: "Price ↓" },
  { key: "subscribers", label: "Subscribers" },
  { key: "recent", label: "Recent" },
];

export default function BrowseCreators() {
  usePageMeta({
    title: "Browse Creators — CheatCraft",
    description: "Discover trusted media creators for your cheat software. Filter by game, video quality, language and content type.",
    canonicalPath: "/creators",
  });
  const { user } = useUser();
  const isAnon = user === null; // undefined = still loading
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: "",
    game: "",
    quality: "",
    service: "",
    language: "",
    min_price: "",
    max_price: "",
    has_mic: null,
    has_facecam: null,
    sort: "rating",
  });

  const params = useMemo(() => {
    const p = { sort: filters.sort };
    if (filters.search) p.search = filters.search;
    if (filters.game) p.game = filters.game;
    if (filters.quality) p.quality = filters.quality;
    if (filters.service) p.service = filters.service;
    if (filters.language) p.language = filters.language;
    if (filters.min_price) p.min_price = Number(filters.min_price);
    if (filters.max_price) p.max_price = Number(filters.max_price);
    if (filters.has_mic !== null) p.has_mic = filters.has_mic;
    if (filters.has_facecam !== null) p.has_facecam = filters.has_facecam;
    return p;
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    listCreators(params)
      .then(setCreators)
      .catch(() => setCreators([]))
      .finally(() => setLoading(false));
  }, [params]);

  const resetFilters = () => setFilters({
    search: "", game: "", quality: "", service: "", language: "",
    min_price: "", max_price: "", has_mic: null, has_facecam: null, sort: "rating",
  });

  const activeFiltersCount =
    (filters.game ? 1 : 0) +
    (filters.quality ? 1 : 0) +
    (filters.service ? 1 : 0) +
    (filters.language ? 1 : 0) +
    (filters.min_price ? 1 : 0) +
    (filters.max_price ? 1 : 0) +
    (filters.has_mic !== null ? 1 : 0) +
    (filters.has_facecam !== null ? 1 : 0);

  return (
    <div data-testid="browse-creators-page" className="max-w-7xl mx-auto px-6 py-12">
      {/* Brand banner */}
      <section
        data-testid="creators-brand-banner"
        className="relative mb-10 overflow-hidden rounded-2xl border border-[#15152a] bg-[#06060c]"
        aria-label="CheatCraft brand banner"
      >
        <img
          src="https://customer-assets.emergentagent.com/job_gaming-marketplace-85/artifacts/346947ri_cheatcraft.webp"
          alt="Your cheat. Our creators. Crafted to perfection."
          loading="eager"
          className="block w-full h-auto select-none pointer-events-none"
          draggable="false"
        />
        {/* Soft fade so the banner sits seamlessly on the dark page */}
        <span aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-b from-transparent to-[#0a0a14]" />
      </section>

      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="cc-overline">Marketplace</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mt-2">Discover Creators</h1>
          <p className="text-sm text-[#a0a0b0] mt-2">Filter by game, quality, language and content type.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#606070]" />
            <input
              data-testid="creators-search-input"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search by username…"
              className="bg-[#0a0a14] border border-[#15152a] rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-[#606070] w-60 cc-focus-ring"
            />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside data-testid="filters-sidebar" className="cc-surface rounded-2xl p-6 h-max sticky top-20">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <SlidersHorizontal size={14} className="text-[#00ff88]" />
              <span className="text-sm text-white font-semibold">Filters</span>
              {activeFiltersCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/25">
                  {activeFiltersCount}
                </span>
              )}
            </div>
            {activeFiltersCount > 0 && (
              <button
                data-testid="filters-reset"
                onClick={resetFilters}
                className="text-[11px] text-[#606070] hover:text-white flex items-center gap-1"
              >
                <X size={12} /> Reset
              </button>
            )}
          </div>

          <FilterSection title="Game">
            <ChipGroup options={GAMES} value={filters.game} onChange={(v) => setFilters((f) => ({ ...f, game: v === f.game ? "" : v }))} testPrefix="filter-game" />
          </FilterSection>
          <FilterSection title="Video Quality">
            <ChipGroup options={QUALITIES} value={filters.quality} onChange={(v) => setFilters((f) => ({ ...f, quality: v === f.quality ? "" : v }))} testPrefix="filter-quality" />
          </FilterSection>
          <FilterSection title="Service">
            <ChipGroup options={SERVICES} value={filters.service} onChange={(v) => setFilters((f) => ({ ...f, service: v === f.service ? "" : v }))} testPrefix="filter-service" />
          </FilterSection>
          <FilterSection title="Language">
            <ChipGroup options={LANGUAGES} value={filters.language} onChange={(v) => setFilters((f) => ({ ...f, language: v === f.language ? "" : v }))} testPrefix="filter-lang" />
          </FilterSection>
          <FilterSection title="Price $">
            <div className="flex items-center gap-2">
              <input data-testid="filter-min-price" type="number" value={filters.min_price} onChange={(e) => setFilters((f) => ({ ...f, min_price: e.target.value }))} placeholder="Min"
                className="w-full bg-[#06060c] border border-[#15152a] rounded-md px-2 py-1.5 text-xs text-white placeholder-[#606070] font-mono" />
              <span className="text-[#606070]">—</span>
              <input data-testid="filter-max-price" type="number" value={filters.max_price} onChange={(e) => setFilters((f) => ({ ...f, max_price: e.target.value }))} placeholder="Max"
                className="w-full bg-[#06060c] border border-[#15152a] rounded-md px-2 py-1.5 text-xs text-white placeholder-[#606070] font-mono" />
            </div>
          </FilterSection>
          <FilterSection title="Equipment">
            <div className="space-y-2">
              <TriSwitch label="Microphone" value={filters.has_mic} onChange={(v) => setFilters((f) => ({ ...f, has_mic: v }))} testId="filter-mic" />
              <TriSwitch label="Facecam" value={filters.has_facecam} onChange={(v) => setFilters((f) => ({ ...f, has_facecam: v }))} testId="filter-facecam" />
            </div>
          </FilterSection>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="text-sm text-[#a0a0b0]">
              <span className="text-white font-mono">{creators.length}</span> creator{creators.length > 1 ? "s" : ""}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  data-testid={`sort-${s.key}`}
                  onClick={() => setFilters((f) => ({ ...f, sort: s.key }))}
                  className={`cc-pill ${filters.sort === s.key ? "active" : ""}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {loading && (
            <div className="grid sm:grid-cols-2 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="cc-surface rounded-2xl p-6 h-64 animate-pulse" />
              ))}
            </div>
          )}

          {!loading && creators.length === 0 && (
            <div data-testid="creators-empty" className="cc-surface rounded-2xl p-12 text-center">
              <div className="text-4xl text-[#606070] mb-3">◌</div>
              <div className="text-white text-lg font-semibold">{activeFiltersCount > 0 ? "No creator matches your filters" : "No creators yet"}</div>
              <div className="text-xs text-[#606070] mt-1">
                {activeFiltersCount > 0
                  ? "Broaden your criteria to see more profiles."
                  : "Be the first — creators who sign up show up right here."}
              </div>
              {activeFiltersCount === 0 && (
                <a
                  href="/signup/creator"
                  data-testid="creators-empty-cta"
                  className="cc-btn-primary rounded-lg px-5 py-2.5 text-sm inline-flex items-center gap-2 mt-5"
                >
                  Sign up as a Creator →
                </a>
              )}
            </div>
          )}

          {!loading && creators.length > 0 && (
            <div data-testid="creators-grid-wrap" className="relative">
              <div
                className={`grid sm:grid-cols-2 gap-6 transition-all duration-500 ${
                  isAnon ? "blur-[6px] opacity-60 pointer-events-none select-none" : ""
                }`}
                aria-hidden={isAnon ? "true" : undefined}
              >
                {creators.map((c) => <CreatorCard key={c.id} c={c} />)}
              </div>

              {isAnon && (
                <div
                  data-testid="creators-paywall"
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                  <div className="pointer-events-auto max-w-md w-[92%] cc-elevated rounded-2xl p-7 border border-[#00ff88]/30 shadow-[0_0_60px_rgba(0,255,136,0.15)] text-center">
                    <div className="w-12 h-12 mx-auto rounded-full bg-[#00ff88]/10 border border-[#00ff88]/30 flex items-center justify-center mb-4">
                      <Lock className="text-[#00ff88]" size={20} />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Sign in to see <span className="text-[#00ff88]">{creators.length} creators</span>
                    </h3>
                    <p className="text-sm text-[#a0a0b0] mb-5 leading-relaxed">
                      Browse portfolios, prices, add-ons and ratings — all unlocked once you create a free account.
                    </p>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <Link
                        to="/signup"
                        data-testid="paywall-signup-btn"
                        className="cc-btn-primary inline-flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-sm"
                      >
                        Create free account <ArrowRight size={14} />
                      </Link>
                      <Link
                        to="/login"
                        data-testid="paywall-login-btn"
                        className="px-4 py-2.5 text-sm border border-[#15152a] bg-[#0a0a14] text-white rounded-lg hover:border-[#1f1f3a] inline-flex items-center gap-1.5"
                      >
                        <Eye size={14} /> I have an account
                      </Link>
                    </div>
                    <div className="text-[11px] text-[#606070] mt-4">
                      Free forever for creators · 10 % platform fee for orders
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const FilterSection = ({ title, children }) => (
  <div className="mb-5 pb-5 border-b border-[#15152a] last:border-0 last:pb-0 last:mb-0">
    <div className="cc-overline mb-2.5">{title}</div>
    {children}
  </div>
);

const ChipGroup = ({ options, value, onChange, testPrefix }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map((o) => (
      <button
        key={o}
        data-testid={`${testPrefix}-${o}`}
        onClick={() => onChange(o)}
        className={`px-2.5 py-1 rounded-md text-[11px] transition-all ${
          value === o
            ? "bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/30"
            : "border border-[#15152a] text-[#a0a0b0] hover:text-white hover:border-[#1f1f3a]"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

const TriSwitch = ({ label, value, onChange, testId }) => (
  <div className="flex items-center justify-between text-xs">
    <span className="text-[#a0a0b0]">{label}</span>
    <div className="flex items-center gap-1" data-testid={testId}>
      {[
        { k: null, l: "Any" },
        { k: true, l: "Yes" },
        { k: false, l: "No" },
      ].map((o) => (
        <button
          key={String(o.k)}
          data-testid={`${testId}-${o.l}`}
          onClick={() => onChange(o.k)}
          className={`px-2 py-0.5 rounded text-[10px] ${
            value === o.k
              ? "bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/30"
              : "border border-[#15152a] text-[#a0a0b0] hover:text-white"
          }`}
        >
          {o.l}
        </button>
      ))}
    </div>
  </div>
);
